import { ReactNode, useMemo } from 'react';
import { Tooltip as AriaTooltip, Focusable, TooltipTrigger } from 'react-aria-components';
import styled, { css } from 'styled-components';
import { colors } from '@karnott/colors';
import { pixelSpacing } from '@karnott/theme';
import { TooltipMessage, TooltipMessageProps } from './TooltipMessage';

type TooltipProps = TooltipMessageProps & {
  /** Content that opens the tooltip on hover */
  content?: ReactNode;
};

/** Display helpful information on hover */
export function Tooltip({
  content,
  message,
  orientation = 'top',
  noWrap = false,
  small = false,
  unstyledContent = false,
}: TooltipProps) {
  const defaultedContent: ReactNode = useMemo(() => (content ? content : '?'), [content]);
  const placement = orientation === 'left' ? 'left top' : orientation === 'right' ? 'right top' : orientation;

  return message ? (
    <TooltipTrigger delay={0} closeDelay={0}>
      <Focusable>
        {/* oxlint-disable-next-line jsx-a11y/role-has-required-aria-props */}
        <TooltipContainer $content={defaultedContent} unstyled={unstyledContent} role="combobox" tabIndex={0}>
          {defaultedContent}
        </TooltipContainer>
      </Focusable>
      <AriaTooltip placement={placement} offset={orientation === 'left' || orientation === 'right' ? 12 : 3}>
        <TooltipMessage message={message} noWrap={noWrap} small={small} managedPositioning />
      </AriaTooltip>
    </TooltipTrigger>
  ) : (
    defaultedContent
  );
}

const TooltipContainer = styled.div<{
  $content: ReactNode;
  unstyled: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: fit-content;
  ${({ unstyled, $content }) =>
    unstyled
      ? null
      : css`
          color: ${colors('white')};
          background-color: ${colors('black')};
          font-size: 10px;
          border-radius: ${typeof $content === 'string' && $content?.length > 1 ? '5px' : '50%'};
          padding: ${
            typeof $content === 'string' && $content?.length > 1
              ? pixelSpacing('xSmall')
              : `${pixelSpacing('xSmall')} ${pixelSpacing('small')}`
          };
        `}
`;
